import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { lazy, useState, type JSX, type ReactNode } from "react";
import type { DocumentId } from "../index";
import type { TrayStore } from "./attachment-tray";
import { readComposerMessage, type ComposerMessage, type PendingStore } from "./composer";
import {
  ComposerContext,
  useComposerValue,
  type ComposerContextValue,
  type ComposerSlots,
} from "./composer-context";
import { createComposerStore } from "./composer-store";
import { ComposerToolbar } from "./ComposerToolbar";
import type { DispatchResult, EditorIntent } from "./handle";
import type { EditorLabels } from "./labels";
import { ChunkBoundary } from "./ChunkBoundary";
import { SendButton } from "./SendButton";

// With the rest of the attachment UI, which a composer that was given no store never fetches.
const ComposerTray = lazy(() => import("./attachments/ComposerTray"));

export interface ComposerChromeProps {
  labels: EditorLabels;
  toolbar: "always" | "never";
  slots: ComposerSlots | undefined;
  readOnly: boolean;
  /** Whether a send control shows at all: `sendButton`, `onSubmit`, `readOnly`, and the pointer. */
  showSend: boolean;
  pending: PendingStore;
  /** The composer's attachments, where it was given a store to keep them in. */
  tray: TrayStore | null;
  submit: () => void;
  documentId: DocumentId;
  /** The host's empty-message policy as it is now; it may be another function on the next render. */
  policy: () => (message: ComposerMessage) => boolean;
  dispatch: (intent: EditorIntent) => DispatchResult;
  /** The editor's `onError`, told when a part fetched on demand does not arrive. */
  onError: (error: Error) => void;
  /** The editable. It is always the same child of the same row, so it is never remounted. */
  children: ReactNode;
}

const given = (slot: ReactNode): boolean => slot !== undefined && slot !== null && slot !== false;

/**
 * What a composer has around its text: a formatting row above, the host's controls and the send
 * control below, and a banner on top. It owns the state those controls show, and hands it to the
 * host's own through context.
 */
export function ComposerChrome(props: ComposerChromeProps): JSX.Element {
  const { labels, slots, readOnly, showSend, pending, policy, documentId, dispatch, tray } = props;
  const [editor] = useLexicalComposerContext();
  const [value] = useState((): ComposerContextValue => ({
    store: createComposerStore({
      editor,
      pending,
      tray,
      isEmpty: () =>
        policy()(readComposerMessage(editor, documentId, tray?.getState().attachments)),
    }),
    dispatch,
  }));
  const toolbar = props.toolbar === "always" && !readOnly;
  // With a row of controls under the text, the send control is the end of that row. Without one it
  // stays beside the text, where a composer that is only a line of text wants it.
  const stacked =
    props.toolbar === "always" || given(slots?.footerStart) || given(slots?.footerEnd);
  const send = showSend ? (
    <Send labels={labels} submit={props.submit} render={slots?.send} />
  ) : null;

  return (
    <ComposerContext value={value}>
      {given(slots?.header) && <div className="ze-composer-header">{slots?.header}</div>}
      <Frame toolbar={toolbar}>
        {toolbar && <ComposerToolbar labels={labels} end={slots?.toolbarEnd} />}
        <div className="ze-composer-row" data-send={showSend && !stacked ? "" : undefined}>
          {props.children}
          {!stacked && send}
        </div>
        {tray !== null && (
          <ChunkBoundary onError={props.onError}>
            <ComposerTray tray={tray} readOnly={readOnly} />
          </ChunkBoundary>
        )}
        {stacked && (given(slots?.footerStart) || given(slots?.footerEnd) || showSend) && (
          <div className="ze-composer-footer">
            <div className="ze-composer-footer-start">{slots?.footerStart}</div>
            <div className="ze-composer-footer-end">
              {slots?.footerEnd}
              {send}
            </div>
          </div>
        )}
      </Frame>
    </ComposerContext>
  );
}

/** Its own component, so a draft becoming empty re-renders two attributes and nothing inside. */
function Frame({ toolbar, children }: { toolbar: boolean; children: ReactNode }): JSX.Element {
  const empty = useComposerValue("empty");
  const pending = useComposerValue("pending");
  return (
    <div
      className="ze-composer-frame"
      data-toolbar={toolbar ? "" : undefined}
      data-empty={empty ? "" : undefined}
      data-pending={pending ? "" : undefined}
    >
      {children}
    </div>
  );
}

function Send(props: {
  labels: EditorLabels;
  submit: () => void;
  render: ComposerSlots["send"];
}): ReactNode {
  const pending = useComposerValue("pending");
  const empty = useComposerValue("empty");
  if (props.render === undefined) return <SendButton labels={props.labels} submit={props.submit} />;
  return props.render({ submit: props.submit, pending, empty });
}
